import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { DisplayedServices } from './getDisplayedServices';
import styles from './searchBar.module.scss';
import ShortcutIcon from '../../../views/common/icons/ShortcutIcon';
import ShortcutTile from './ShortcutTile';
import type { Shortcut } from '../shortcuts/type';
import type { Service } from "@/redux/types.ts";
import { ClockClockwiseIcon } from '@phosphor-icons/react/dist/csr/ClockClockwise';
import { GearIcon } from '@phosphor-icons/react/dist/csr/Gear';
import { SquaresFourIcon } from '@phosphor-icons/react/dist/csr/SquaresFour';
import ServiceTile from './ServiceTile';
import { SEARCH_BAR_MIN_QUERY_LENGTH } from './SearchBar.tsx';
import Tooltip from '@mui/material/Tooltip';
import Loader from '../../../views/common/loader/Loader.tsx';
import IconButton from '@mui/material/IconButton';
import { ArrowRightIcon } from '@phosphor-icons/react/dist/csr/ArrowRight';
import { useAgentChatUi } from "@/context/agent/useAgentChatUi.ts";

type SearchResultsProps = {
  content: DisplayedServices;
  onShortcutClick: (shortcut: Shortcut) => void;
  onServiceClick: (service: Service) => void;
  query: string;
  isAgentLoading?: boolean;
  agentResponse?: string | null;
  agentError?: boolean;
  closeBar?: () => void;
};

const SearchResults: FC<SearchResultsProps> = ({
  content,
  onShortcutClick,
  onServiceClick,
  query,
  isAgentLoading = false,
  agentResponse = null,
  agentError,
  closeBar,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'searchBar' });
  const { mostUsed, services, shortcuts } = content;
  const { open } = useAgentChatUi();

  const handleSeeMore = () => {
    open();
    closeBar?.();
  };

  const hasNoResults = !shortcuts?.length && !services?.length && !mostUsed?.length;

  const onPreferencesNavigate = () => {
    window.location.replace('/users/preferences#recent-services');
  };

  return (
    <>
      {isAgentLoading && (
        <div className={styles.agentLoader} role="status" aria-label="Loading">
          <Loader />
        </div>
      )}
      {agentError && (
        <div className={styles.agentError} role="alert">
          {t('askAgentError')}
        </div>
      )}
      {agentResponse && (
        <div className={styles.agentResponseWrapper}>
          <div className={styles.agentResponse} dangerouslySetInnerHTML={{ __html: agentResponse }} />
          <IconButton className={styles.agentResponseAction} onClick={handleSeeMore}>
            {t('seeMore')}
            <ArrowRightIcon weight="bold" size={16} />
          </IconButton>
        </div>
      )}
      {mostUsed && mostUsed.length > 0 && (
        <>
          <div data-testid="most-used-services" className={styles.subTitle}>
            <ClockClockwiseIcon size={16} />
            {t('mostUsed')}
            <Tooltip placement="bottom-end" classes={{ popper: styles.tooltip }} title={t('preferences')}>
              <span className={styles.preferencesIcon} onClick={onPreferencesNavigate}>
                <GearIcon size={16} />
              </span>
            </Tooltip>
          </div>
          {mostUsed.map(service => (
            <ServiceTile key={`mu-${service.path}`} service={service} onClick={onServiceClick} />
          ))}
        </>
      )}

      {services?.length ? (
        <>
          <div data-testid="services-results" className={styles.subTitle}>
            <SquaresFourIcon size={16} />
            {query.length >= SEARCH_BAR_MIN_QUERY_LENGTH ? t('services') : t('serviceAll')}
          </div>
          {services.map(service => (
            <ServiceTile key={service.path} service={service} onClick={onServiceClick} query={query} />
          ))}
        </>
      ) : null}
      {shortcuts?.length ? (
        <>
          <div data-testid="shortcuts-results" className={styles.subTitle}>
            <ShortcutIcon width="16px" height="16px" color="#323330" />
            {t('shortcuts')}
          </div>
          {shortcuts.map(shortcut => (
            <ShortcutTile key={shortcut.id} shortcut={shortcut} onClick={onShortcutClick} query={query} />
          ))}
        </>
      ) : null}
      {hasNoResults && !agentResponse && !isAgentLoading && !agentError && (
        <div className={styles.subTitle}>{t('noResults')}</div>
      )}
    </>
  );
};

export default SearchResults;
